/**
 * SPA hooks for the customer-facing shell: Feed (/app), Dossier
 * (/app/brands/:id), and Pipeline (/app/pipeline).
 *
 * Reads come from GET /api/feed (ranked dossier cards for a category),
 * GET /api/feed/:id (single-brand dossier), and GET /api/claims (this
 * tenant's outreach pipeline). Writes: claiming a brand goes through
 * POST /api/claims and advancing/dropping through PATCH /api/claims/:id;
 * passing on a brand REUSES the existing brand-decision mutation from
 * lib/brands.ts (PATCH /api/brands/:name/decision, decision:'rejected')
 * rather than standing up a duplicate decision endpoint — see
 * apps/api/src/routes/brands.ts, which already treats 'rejected' as the
 * durable pass signal.
 */
import { queryOptions, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ClaimResponse,
  CreateClaimRequest,
  DiscoverSupplierForDossierResponse,
  DossierResponse,
  FeedResponse,
  ListClaimsResponse,
  UpdateClaimRequest,
  type BrandClaimStatus,
} from '@sourcito/schemas';
import { apiFetch } from './api';
import { useBrandDecisionMutation } from './brands';

export function feedQuery(categoryId: string | null) {
  return queryOptions({
    queryKey: ['feed', categoryId] as const,
    queryFn: () =>
      apiFetch<FeedResponse>(`/api/feed?categoryId=${encodeURIComponent(categoryId!)}`, {
        responseSchema: FeedResponse,
      }),
    enabled: Boolean(categoryId),
    staleTime: 15_000,
  });
}

export function useFeed(categoryId: string | null) {
  return useQuery(feedQuery(categoryId));
}

// ─── Dossier (single-brand detail) ─────────────────────────────────────────

export function dossierQuery(brandCacheId: string | null) {
  return queryOptions({
    queryKey: ['dossier', brandCacheId] as const,
    queryFn: () =>
      apiFetch<DossierResponse>(`/api/feed/${brandCacheId}`, {
        responseSchema: DossierResponse,
      }),
    enabled: Boolean(brandCacheId),
  });
}

export function useDossier(brandCacheId: string | null) {
  return useQuery(dossierQuery(brandCacheId));
}

/**
 * Kicks off catalog-shaped discovery for this brand (POST /api/feed/:id/
 * discover-supplier). Doesn't invalidate ['dossier'] itself — the run is
 * background/async, so there's nothing new to show until it completes; the
 * caller polls the returned runId via the existing discovery-run hook and
 * invalidates supplier-candidates once it finishes.
 */
export function useDiscoverSupplier(brandCacheId: string) {
  return useMutation({
    mutationFn: () =>
      apiFetch<DiscoverSupplierForDossierResponse>(`/api/feed/${brandCacheId}/discover-supplier`, {
        method: 'POST',
        body: '{}',
        responseSchema: DiscoverSupplierForDossierResponse,
      }),
  });
}

export function useClaimBrand() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { brandCacheId: string }) =>
      apiFetch<ClaimResponse>('/api/claims', {
        method: 'POST',
        body: JSON.stringify(CreateClaimRequest.parse(input)),
        responseSchema: ClaimResponse,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['feed'] });
      // The dossier embeds claimStatus too — without this, claiming FROM
      // the dossier leaves the header stale (Claim button lingers,
      // Mark-as-contacted stays disabled) until an incidental refetch.
      qc.invalidateQueries({ queryKey: ['dossier'] });
      // The Pipeline screen reads ['claims'] — without this, a brand
      // claimed from the Feed/Dossier doesn't appear there until an
      // incidental refetch.
      qc.invalidateQueries({ queryKey: ['claims'] });
    },
  });
}

/**
 * Thin wrapper around useBrandDecisionMutation — adds the feed-list
 * invalidation that plain brand-decision callers (BrandsPage, BrandDetailPage)
 * don't need, without duplicating the mutation itself. `name` must be the
 * brand's `displayName` (the decision route normalizes+matches on that,
 * see brands.ts's `normalizeBrandName`).
 */
export function usePassBrand() {
  const qc = useQueryClient();
  const decision = useBrandDecisionMutation();
  const invalidateFeed = () => qc.invalidateQueries({ queryKey: ['feed'] });
  return {
    ...decision,
    // `reason` lands in brand_decisions.notes — free-text training data for
    // automating the filter later (Faraz, 2026-08-17: "this is gold").
    mutate: (input: { name: string; reason?: string }) =>
      decision.mutate(
        { name: input.name, decision: 'rejected', notes: input.reason || undefined },
        { onSuccess: invalidateFeed }
      ),
    // Overridden too so no call path can skip the feed invalidation.
    mutateAsync: (input: { name: string; reason?: string }) =>
      decision.mutateAsync(
        { name: input.name, decision: 'rejected', notes: input.reason || undefined },
        { onSuccess: invalidateFeed }
      ),
  };
}

/**
 * PATCH /api/claims/:id — advance (or drop) a claim's status. Forward-only
 * on the server (see routes/claims.ts's transition table); this hook just
 * forwards whatever status the caller requests and lets the 400
 * ILLEGAL_TRANSITION surface as a mutation error if it's not legal.
 *
 * Invalidates ['feed'], ['dossier'], and ['claims'] — all three embed
 * claimStatus/status and need to stay coherent without a manual refresh.
 */
export function useAdvanceClaim() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { id: string; status: BrandClaimStatus }) =>
      apiFetch<ClaimResponse>(`/api/claims/${input.id}`, {
        method: 'PATCH',
        body: JSON.stringify(UpdateClaimRequest.parse({ status: input.status })),
        responseSchema: ClaimResponse,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['feed'] });
      qc.invalidateQueries({ queryKey: ['dossier'] });
      qc.invalidateQueries({ queryKey: ['claims'] });
    },
    // On ILLEGAL_TRANSITION (stale tab racing the server's guard) resync the
    // board so the inline error is accompanied by corrected column state.
    onError: () => {
      qc.invalidateQueries({ queryKey: ['claims'] });
    },
  });
}

// ─── Pipeline (claims list) ─────────────────────────────────────────────

/** GET /api/claims — this tenant's claims, active-first (see routes/claims.ts). */
export function claimsQuery() {
  return queryOptions({
    queryKey: ['claims'] as const,
    queryFn: () =>
      apiFetch<ListClaimsResponse>('/api/claims', {
        responseSchema: ListClaimsResponse,
      }),
  });
}

export function useClaims() {
  return useQuery(claimsQuery());
}
